import { Component, EventEmitter, inject, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ScreenService } from '../../services/screen/screen.service';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { AuthService } from '../../services/auth/auth.service';
import { TEXT_CONSTANTS } from '../../constants/text-constants';
import { map } from 'rxjs';

/**
 * Bottom navigation bar for mobile view.
 * Shows 4 navigation items + a center FAB button for new transactions.
 * Includes a fullscreen menu overlay for secondary navigation items.
 */
@Component({
    selector: 'bottom-bar',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './bottom-bar.component.html',
    styleUrl: './bottom-bar.component.css'
})
export class BottomBarComponent {
    private _screenService = inject(ScreenService);
    private _authService = inject(AuthService);
    private _permissionsService = inject(PermissionsService);

    /** Emits when the FAB button is tapped. */
    @Output() fabTapped = new EventEmitter<void>();

    TEXT = TEXT_CONSTANTS;
    isMenuOpen = false;

    /** Primary items shown directly in the bottom bar. */
    primaryItems = [
        { title: TEXT_CONSTANTS.SIDEBAR.DASHBOARD, icon: 'home', route: '/' },
        { title: TEXT_CONSTANTS.SIDEBAR.TRANSACTIONS, icon: 'payments', route: '/transactions' },
        // FAB goes here (index 2 in the bar)
        { title: TEXT_CONSTANTS.SIDEBAR.CARDS, icon: 'credit_card', route: '/cards' },
        { title: TEXT_CONSTANTS.BOTTOM_BAR.MENU, icon: 'menu', route: null },
    ];

    /** Secondary items shown in the fullscreen menu overlay. */
    private allMenuItems = [
        { title: TEXT_CONSTANTS.SIDEBAR.FIXED_EXPENSES, icon: 'receipt_long', route: '/fixed-expenses' },
        { title: TEXT_CONSTANTS.SIDEBAR.GROUPS, icon: 'group', route: '/groups' },
        { title: TEXT_CONSTANTS.SIDEBAR.WALLETS, icon: 'savings', route: '/wallets' },
        { title: TEXT_CONSTANTS.SIDEBAR.ANALYTICS, icon: 'monitoring', route: '/analytics' },
        { title: TEXT_CONSTANTS.SIDEBAR.SETTINGS, icon: 'settings', route: '/settings' },
    ];

    /** Filtered menu items based on user permissions. */
    menuItems$ = this._authService.userRole.pipe(
        map(() => this.allMenuItems.filter(item => this._permissionsService.hasAccess(item.route)))
    );

    /** Checks if the current view is mobile. */
    isMobile(): boolean {
        return this._screenService.isMobile();
    }

    /** Opens the fullscreen menu overlay. */
    openMenu(): void {
        this.isMenuOpen = true;
    }

    /** Closes the fullscreen menu overlay. */
    closeMenu(): void {
        this.isMenuOpen = false;
    }

    /** Handles FAB button tap. */
    onFabTap(): void {
        this.fabTapped.emit();
    }

    /** Handles navigation item click — closes menu if open. */
    onNavItemClick(item: { route: string | null }): void {
        if (item.route === null) {
            this.isMenuOpen = !this.isMenuOpen; // Toggle menu state
        } else {
            this.closeMenu();
        }
    }
}
